import type { CSSProperties, FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { FigLabel } from '@/components/generals/fig-label'
import { PointCloud } from '@/components/generals/point-cloud'
import { Reveal } from '@/components/generals/reveal'
import { TextLink } from '@/components/generals/text-link'

import { CAPABILITIES } from '@/constants/platform'

interface StageHandle {
    setScene: (index: number) => void
    dispose: () => void
}

const delay = (value: number): CSSProperties => ({ '--delay': `${value}ms` } as CSSProperties)

const STAGE_LABEL = 'One point-cloud building on the right that rearranges itself as you scroll: a timber library for Capture, a housing block for Model, a concert hall for Monitor and the studio archive lattice for Archive.'

/**
 * Home section 07: the left half scrolls through the four platform steps while one large point
 * cloud on the right stays pinned and morphs into the matching building.
 */
export const PlatformPinned: FC = (): ReactNode => {
    const stageRef = useRef<HTMLDivElement>(null)
    const stepRefs = useRef<Array<HTMLElement | null>>([])
    const handleRef = useRef<StageHandle | null>(null)
    const activeRef = useRef(0)
    const [active, setActive] = useState(0)
    const [failed, setFailed] = useState(false)

    useEffect(() => {
        const container = stageRef.current

        if (!container) return

        const query = window.matchMedia('(min-width: 56.25rem)')

        let cancelled = false
        let near = false

        const create = (): void => {
            if (handleRef.current || cancelled || !query.matches || !near) return

            import('@/lib/cloud/morph-stage')
                .then(({ MorphStage }) => {
                    if (cancelled || handleRef.current || !stageRef.current || !query.matches) return

                    try {
                        handleRef.current = new MorphStage(stageRef.current, activeRef.current)
                    }
                    catch (error) {
                        console.error('The platform stage could not start:', error instanceof Error ? error.message : error)
                        setFailed(true)
                    }
                })
                .catch(() => setFailed(true))
        }

        const destroy = (): void => {
            handleRef.current?.dispose()
            handleRef.current = null
        }

        const observer = new IntersectionObserver(entries => {
            near = entries.some(entry => entry.isIntersecting)

            if (near) create()
        }, { rootMargin: '600px 0px' })

        const onQuery = (): void => {
            if (query.matches) create()
            else destroy()
        }

        observer.observe(container)
        query.addEventListener('change', onQuery)

        return () => {
            cancelled = true
            observer.disconnect()
            query.removeEventListener('change', onQuery)
            destroy()
        }
    }, [])

    useEffect(() => {
        activeRef.current = active
        handleRef.current?.setScene(active)
    }, [active])

    useEffect(() => {
        const steps = stepRefs.current.filter((step): step is HTMLElement => step !== null)

        if (steps.length === 0) return

        const observer = new IntersectionObserver(entries => {
            for (const entry of entries) {
                if (!entry.isIntersecting) continue

                const index = Number((entry.target as HTMLElement).dataset.step)

                if (Number.isFinite(index)) setActive(index)
            }
        }, { rootMargin: '-48% 0px -48% 0px' })

        steps.forEach(step => observer.observe(step))

        return () => observer.disconnect()
    }, [])

    const current = CAPABILITIES[active]

    return (
        <section id='platform' aria-labelledby='platform-title' className='relative border-t border-hairline'>
            <Reveal className='px-gutter pb-6 pt-20 desk:pb-0 desk:pt-24'>
                <FigLabel fig='07' title='Platform' detail='Survey to archive' note='Four instruments / one live model' />

                <h2 id='platform-title' className='display-md mt-8 max-w-[18ch]'>
                    <span className='reveal-line'>
                        <span className='reveal-inner text-ink' style={delay(80)}>
                            From the first scan
                        </span>
                    </span>

                    <span className='reveal-line'>
                        <span className='reveal-inner text-dim' style={delay(170)}>
                            to the final decade.
                        </span>
                    </span>
                </h2>
            </Reveal>

            <div className='desk:grid desk:grid-cols-2'>
                <div>
                    <ol className='relative'>
                        {CAPABILITIES.map((capability, index) => {
                            const last = capability.headline.length - 1
                            const isActive = index === active

                            return (
                                <li
                                    key={capability.id}
                                    ref={element => {
                                        stepRefs.current[index] = element
                                    }}
                                    data-step={index}
                                    aria-current={isActive ? 'step' : undefined}
                                    className='border-t border-hairline desk:flex desk:min-h-svh desk:items-center desk:border-t-0'
                                >
                                    <PointCloud
                                        shape={capability.shape}
                                        label={capability.previewLabel}
                                        budget='thumb'
                                        rotate='always'
                                        rotateSpeed={5}
                                        effects
                                        frame
                                        className='h-60 w-full border-b border-hairline sm:h-72 desk:hidden'
                                    >
                                        <p className='hud pointer-events-none absolute left-gutter top-3 z-[2] text-mute' aria-hidden='true'>
                                            {capability.caption}
                                        </p>
                                    </PointCloud>

                                    <Reveal
                                        className={cn(
                                            'w-full px-gutter py-12 transition-opacity duration-700 ease-instrument desk:max-w-[620px] desk:py-24',
                                            !isActive && 'desk:opacity-35'
                                        )}
                                    >
                                        <p className='hud reveal-fade flex items-center gap-3 text-mute'>
                                            <span className={cn('transition-colors duration-500', isActive ? 'text-signal' : 'text-mute')}>
                                                {capability.number}
                                            </span>

                                            <span className='h-px w-8 bg-hairline' aria-hidden='true' />

                                            <span className='text-ink'>
                                                {capability.name}
                                            </span>
                                        </p>

                                        <h3 className='display-md mt-6'>
                                            {capability.headline.map((line, lineIndex) => (
                                                <span key={line} className='reveal-line'>
                                                    <span className={cn('reveal-inner', lineIndex === last ? 'text-dim' : 'text-ink')} style={delay(80 + lineIndex * 90)}>
                                                        {line}
                                                    </span>
                                                </span>
                                            ))}
                                        </h3>

                                        <p className='reveal-fade mt-6 text-[15px] leading-[1.6] text-ink/85' style={delay(260)}>
                                            <span className='block'>
                                                {capability.summary[0]}
                                            </span>

                                            <span className='block'>
                                                {capability.summary[1]}
                                            </span>
                                        </p>

                                        <ul className='reveal-fade mt-8 max-w-[440px] border-t border-hairline' style={delay(340)}>
                                            {capability.homeSpecs.map(spec => (
                                                <li key={spec} className='hud border-b border-hairline py-3 text-ink'>
                                                    {spec}
                                                </li>
                                            ))}
                                        </ul>
                                    </Reveal>
                                </li>
                            )
                        })}
                    </ol>

                    <div className='border-t border-hairline px-gutter pb-16 pt-8 desk:border-t-0 desk:pb-24 desk:pt-0'>
                        <TextLink href='/platform'>
                            The full platform
                        </TextLink>
                    </div>
                </div>

                <div className='relative hidden border-l border-hairline desk:block'>
                    <div className='sticky top-0 h-svh'>
                        <div ref={stageRef} role='img' aria-label={STAGE_LABEL} data-crosshair='' className='absolute inset-0 bg-background' />

                        <div className='pointer-events-none absolute inset-0 z-[2]' aria-hidden='true'>
                            <p className='hud absolute left-6 top-20 text-ink'>
                                Fig. 07.{active + 1} / {current.name}
                            </p>

                            <p className='hud absolute right-gutter top-20 text-right text-mute'>
                                {current.caption}
                            </p>

                            <div className='absolute bottom-8 left-6 right-gutter flex items-center gap-4'>
                                {CAPABILITIES.map((capability, index) => (
                                    <span key={capability.id} className='flex flex-1 flex-col gap-2'>
                                        <span className={cn('h-px w-full transition-colors duration-500', index === active ? 'bg-signal' : 'bg-hairline')} />

                                        <span className={cn('hud transition-colors duration-500', index === active ? 'text-ink' : 'text-mute')}>
                                            {capability.number} {capability.name}
                                        </span>
                                    </span>
                                ))}
                            </div>
                        </div>

                        {failed && (
                            <div className='absolute inset-0 z-[3] grid place-items-center px-6'>
                                <p className='hud text-center text-mute'>
                                    3D view unavailable in this browser
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    )
}
